'use client'

/* MasjidOS Basic — CLIENT-SIDE DEMO dashboard (Phase B).
 *
 * Fetches the demo masjid's seed data ONCE (GET /api/demo/dashboard, read-only + public), then EVERY
 * edit below is in-memory React state — there is NO fetch/POST/PATCH in any edit handler. On refresh
 * the component re-mounts and re-fetches the seed, so in-memory edits evaporate (THIS is the reset;
 * no reset job needed). A persistent DEMO banner + per-action "(demo — not saved)" labels make it
 * unmissable that nothing here is real or saved.
 *
 * The real Basic dashboard pages are monolithic API-coupled client components (no reusable
 * presentational split), so per the spec this is a thin PARALLEL UI that mimics their layout.
 */

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Bell, Clock3, IndianRupee, Plus, Trash2 } from 'lucide-react'

type Announcement = {
  id: string
  title: string
  body: string
  category: string
  isPublished: boolean
  priority: string
  updatedAt: string | null
  photoUrl: string | null
}
type PrayerSlot = { adhan?: string; iqamah?: string; notes?: string }
type JumuahSlot = { label?: string; time?: string; notes?: string }
type Upi = { upiId: string | null; upiName: string | null }

const PRAYERS: Array<{ key: string; label: string }> = [
  { key: 'fajr', label: 'Fajr' },
  { key: 'dhuhr', label: 'Dhuhr' },
  { key: 'asr', label: 'Asr' },
  { key: 'maghrib', label: 'Maghrib' },
  { key: 'isha', label: 'Isha' },
]

export default function DemoDashboardClient() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [masjidName, setMasjidName] = useState('Sample Masjid (Demo)')
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [timings, setTimings] = useState<Record<string, PrayerSlot>>({})
  const [jumuah, setJumuah] = useState<JumuahSlot[]>([])
  const [upi, setUpi] = useState<Upi>({ upiId: '', upiName: '' })

  // Add-announcement form (in-memory only).
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')

  // Per-action confirmation — the "(demo — not saved)" label after any edit.
  const [lastAction, setLastAction] = useState<string | null>(null)
  const newIdRef = useRef(0)
  const note = (msg: string) => setLastAction(msg)

  // ── READ ONCE (the only network call in the whole demo) ──
  useEffect(() => {
    let alive = true
    fetch('/api/demo/dashboard')
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return
        if (d?.error) { setError(d.error); setLoading(false); return }
        setMasjidName(d.masjidName || 'Sample Masjid (Demo)')
        setAnnouncements(Array.isArray(d.announcements) ? d.announcements : [])
        setTimings(d.prayerTimes?.timings && typeof d.prayerTimes.timings === 'object' ? d.prayerTimes.timings : {})
        setJumuah(Array.isArray(d.prayerTimes?.jumuah) ? d.prayerTimes.jumuah : [])
        setUpi({ upiId: d.upi?.upiId ?? '', upiName: d.upi?.upiName ?? '' })
        setLoading(false)
      })
      .catch(() => { if (alive) { setError('Demo is temporarily unavailable.'); setLoading(false) } })
    return () => { alive = false }
  }, [])

  // ── EDIT HANDLERS — every one is setState only. NO fetch, NO POST, nothing leaves the browser. ──
  function addAnnouncement() {
    const t = title.trim(), b = body.trim()
    if (t.length < 3 || b.length < 3) { note('Enter a title and a message.'); return }
    setAnnouncements((prev) => [
      { id: `demo-new-${++newIdRef.current}`, title: t, body: b, category: 'general', isPublished: true, priority: 'normal', updatedAt: new Date().toISOString(), photoUrl: null },
      ...prev,
    ])
    setTitle(''); setBody('')
    note('Announcement added (demo — not saved)')
  }
  function deleteAnnouncement(id: string) {
    setAnnouncements((prev) => prev.filter((a) => a.id !== id))
    note('Announcement removed (demo — not saved)')
  }
  function editTiming(key: string, field: 'adhan' | 'iqamah', value: string) {
    setTimings((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }))
    note('Prayer time changed (demo — not saved)')
  }
  function editJumuah(idx: number, field: 'label' | 'time', value: string) {
    setJumuah((prev) => prev.map((j, i) => (i === idx ? { ...j, [field]: value } : j)))
    note('Jumu‘ah time changed (demo — not saved)')
  }
  function editUpi(field: 'upiId' | 'upiName', value: string) {
    setUpi((prev) => ({ ...prev, [field]: value }))
    note('UPI details changed (demo — not saved)')
  }

  const card: React.CSSProperties = { background: '#fff', borderRadius: 16, padding: 22, marginBottom: 18, boxShadow: '0 2px 12px rgba(11,19,43,.06)', border: '1px solid #eee' }
  const lbl: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: '#5a6072', display: 'block', marginBottom: 5 }
  const inp: React.CSSProperties = { width: '100%', border: '1.5px solid #e2e2e2', borderRadius: 9, padding: '9px 11px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }
  const h2: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15.5, fontWeight: 800, color: '#0b132b', margin: '0 0 14px' }
  const demoTag = <span style={{ fontSize: 11, fontWeight: 700, color: '#1f7a5c', marginLeft: 8 }}>(demo — not saved)</span>

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 16px 64px' }}>
      {/* PERSISTENT DEMO BANNER — unmissable, always visible */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#0b132b', color: '#fff', borderRadius: 0, padding: '12px 16px', margin: '0 -16px 20px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, lineHeight: 1.4 }}>
        <span style={{ fontSize: 18 }}>🔷</span>
        <span><strong>DEMO</strong> — this is a sample dashboard. <strong>Sample Masjid (Demo)</strong> is not a real masjid. Changes here are for demonstration only and are <strong>NOT saved</strong>.</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0b132b', margin: 0 }}>{masjidName} — MasjidOS Basic</h1>
        <Link href="/" style={{ fontSize: 13, color: '#1f7a5c', fontWeight: 600, textDecoration: 'none' }}>← Back</Link>
      </div>
      {lastAction && (
        <div style={{ background: '#eaf6f1', color: '#1f7a5c', borderRadius: 9, padding: '8px 12px', fontSize: 12.5, fontWeight: 600, marginBottom: 16 }}>{lastAction}</div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: '#888' }}>Loading the sample dashboard…</div>
      ) : error ? (
        <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'center', color: '#B23232' }}>
          <AlertTriangle size={18} /> {error}
        </div>
      ) : (
        <>
          {/* ── ANNOUNCEMENTS ── */}
          <div style={card}>
            <h2 style={h2}><Bell size={17} /> Announcements {demoTag}</h2>
            <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
              <div><label style={lbl}>Title</label><input style={inp} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Jumu‘ah reminder" maxLength={120} /></div>
              <div><label style={lbl}>Message</label><textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write the announcement…" maxLength={2000} /></div>
              <button type="button" onClick={addAnnouncement} style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1f7a5c', color: '#fff', border: 0, borderRadius: 9, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                <Plus size={15} /> Add announcement
              </button>
            </div>
            {announcements.length === 0 ? (
              <p style={{ color: '#888', fontSize: 13, margin: 0 }}>No announcements yet.</p>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {announcements.map((a) => (
                  <div key={a.id} style={{ border: '1px solid #eee', borderRadius: 11, padding: '11px 13px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 800, color: '#0b132b', fontSize: 14 }}>{a.title}</div>
                      <div style={{ color: '#555', fontSize: 12.5, marginTop: 3, lineHeight: 1.5 }}>{a.body}</div>
                    </div>
                    <button type="button" onClick={() => deleteAnnouncement(a.id)} aria-label="Remove" style={{ background: 'none', border: 0, color: '#B23232', cursor: 'pointer', alignSelf: 'flex-start' }}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── PRAYER TIMES ── */}
          <div style={card}>
            <h2 style={h2}><Clock3 size={17} /> Prayer times {demoTag}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {PRAYERS.map(({ key, label }) => (
                <div key={key} style={{ border: '1px solid #eee', borderRadius: 11, padding: 11 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0b132b', marginBottom: 8 }}>{label}</div>
                  <label style={lbl}>Adhan</label>
                  <input style={{ ...inp, marginBottom: 8 }} type="time" value={timings[key]?.adhan || ''} onChange={(e) => editTiming(key, 'adhan', e.target.value)} />
                  <label style={lbl}>Iqamah</label>
                  <input style={inp} type="time" value={timings[key]?.iqamah || ''} onChange={(e) => editTiming(key, 'iqamah', e.target.value)} />
                </div>
              ))}
            </div>
            {jumuah.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <label style={lbl}>Jumu‘ah</label>
                <div style={{ display: 'grid', gap: 8 }}>
                  {jumuah.map((j, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8 }}>
                      <input style={{ ...inp, flex: 2 }} value={j.label || ''} onChange={(e) => editJumuah(i, 'label', e.target.value)} placeholder="Label" />
                      <input style={{ ...inp, flex: 1 }} type="time" value={j.time || ''} onChange={(e) => editJumuah(i, 'time', e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── UPI / DONATIONS ── */}
          <div style={card}>
            <h2 style={h2}><IndianRupee size={17} /> Donation UPI {demoTag}</h2>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
              <div><label style={lbl}>UPI ID</label><input style={inp} value={upi.upiId || ''} onChange={(e) => editUpi('upiId', e.target.value)} placeholder="name@bank" /></div>
              <div><label style={lbl}>UPI name</label><input style={inp} value={upi.upiName || ''} onChange={(e) => editUpi('upiName', e.target.value)} placeholder="Account holder" /></div>
            </div>
            <p style={{ color: '#888', fontSize: 11.5, marginTop: 10, marginBottom: 0 }}>In a real masjid dashboard this UPI shows on the public page once verified. Here it’s a sample — nothing is saved or shown publicly.</p>
          </div>

          <p style={{ textAlign: 'center', color: '#999', fontSize: 12, marginTop: 8 }}>Refresh the page to reset the demo to its starting state.</p>
        </>
      )}
    </div>
  )
}
